import { type ResponseData, useApiQuery, useTeam } from '@vrplatform/data';
import type { Maybe } from '@vrplatform/utils';
import { useDebouncedValue } from '@mantine/hooks';
import { mapExpenseRow } from '../list/expenses/useExpenseTableQuery';

export type Expense = ReturnType<typeof mapExpenseRow> & {
  attachments: ResponseData<'get:/transactions/{id}'>['attachments'];
};

export const STORYBOOK_EXPENSE_DETAIL_QUERY_KEY = 'expense-detail';

export function useExpenseQuery(
  expenseId: Maybe<string>,
  view: 'detail' | 'drawer'
) {
  const [{ id: teamId }] = useTeam();

  const query = useApiQuery(
    async ($api, signal, args) => {
      if (!args.id)
        return {
          expense: null,
        };

      const expense = await $api
        .GET('/transactions/{id}', {
          params: {
            path: {
              id: args.id,
            },
          },
          signal,
        })
        .then((result) => {
          if (result.error) throw result.error;
          const mapped = mapExpenseRow(
            result.data as ResponseData<'get:/transactions'>['data'][number]
          );

          return {
            ...mapped,
            attachments: result.data.attachments ?? [],
          };
        });

      return { expense };
    },
    {
      skip: !expenseId,
      queryKeys: [`expense-detail-${view}`],
      queryGroup: 'expenses',
      uniqueQueryKey: 'expense-detail',
      storybookQueryKey: STORYBOOK_EXPENSE_DETAIL_QUERY_KEY,
      variables: {
        id: expenseId,
        teamId,
      },
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}
